import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { downloadResource } from '../../utils/resourceDownload';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ROW = 'flex w-full min-h-[48px] items-start gap-3 rounded-lg px-3 py-3 text-left';
const LINK_ROW = `${ROW} transition-colors hover:bg-background-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary`;

const ExternalIcon = () => (
  <svg aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M9 7h8v8" />
  </svg>
);

const DocumentIcon = () => (
  <svg aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const Spinner = () => (
  <span aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
);

const SourceText = ({ source }) => (
  <span className="min-w-0 flex-1">
    <span className="line-clamp-2 font-medium text-text-primary">{source.title}</span>
    {source.label && <span className="mt-0.5 block text-sm text-text-secondary">{source.label}</span>}
  </span>
);

// overflow:hidden on a scroller, padded by its scrollbar width so the page
// behind the backdrop doesn't shift sideways. Returns the undo.
const lockScroll = (el) => {
  const { overflow, paddingRight } = el.style;
  const scrollbar = el === document.body
    ? window.innerWidth - document.documentElement.clientWidth
    : el.offsetWidth - el.clientWidth;
  if (scrollbar > 0) {
    el.style.paddingRight = `${parseFloat(window.getComputedStyle(el).paddingRight) + scrollbar}px`;
  }
  el.style.overflow = 'hidden';
  return () => {
    el.style.overflow = overflow;
    el.style.paddingRight = paddingRight;
  };
};

// html { scrollbar-gutter: stable } (tailwind.css) keeps a scrollbar-wide
// strip at the right edge that fixed elements can't cover on classic-
// scrollbar desktops, so the backdrop and drawer would stop short of it.
// While open the gutter goes and body is padded by its width instead, so
// nothing behind the backdrop moves. Returns the undo.
const releaseScrollbarGutter = () => {
  const html = document.documentElement;
  const { body } = document;
  const width = html.getBoundingClientRect().width;
  const gutter = width > 0 ? window.innerWidth - width : 0;
  // Only a scrollbar-sized gap (0 with overlay scrollbars, or when zoomed).
  if (!(gutter > 0 && gutter < 50)) return () => {};
  const { scrollbarGutter } = html.style;
  const { paddingRight } = body.style;
  html.style.scrollbarGutter = 'auto';
  body.style.paddingRight = `${parseFloat(window.getComputedStyle(body).paddingRight) + gutter}px`;
  return () => {
    html.style.scrollbarGutter = scrollbarGutter;
    body.style.paddingRight = paddingRight;
  };
};

/**
 * The sources behind one AI answer: a bottom sheet below md, a right-hand
 * drawer from md up. A modal dialog — focus moves in and is trapped, the app
 * behind is inert and doesn't scroll, Esc and a backdrop click close it, and
 * focus returns to returnFocusRef (the "Vis kilder" button).
 *
 * Rows by link type: external → the URL in a new tab; resource → opened the
 * way the Resources page opens it (downloadResource); null → title and label
 * only, plus a note that some sources can't be opened here.
 */
const SourcesPanel = ({ sources, onClose, returnFocusRef }) => {
  const panelRef = useRef(null);
  const headingId = useId();
  const [openingKey, setOpeningKey] = useState(null);

  useEffect(() => {
    const panel = panelRef.current;
    const returnTo = returnFocusRef && returnFocusRef.current;
    const appRoot = document.getElementById('root');
    const makeInert = appRoot && !appRoot.contains(panel) && !appRoot.hasAttribute('inert');
    if (makeInert) appRoot.setAttribute('inert', '');
    const scroller = document.getElementById('main-scroll-container');
    const unlocks = [releaseScrollbarGutter(), lockScroll(document.body), scroller && lockScroll(scroller)];
    panel.focus();
    return () => {
      if (makeInert) appRoot.removeAttribute('inert');
      // Reverse order: the body padding is set twice and must unwind in turn.
      unlocks.filter(Boolean).reverse().forEach((unlock) => unlock());
      if (returnTo && returnTo.isConnected) returnTo.focus();
    };
  }, [returnFocusRef]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      const focusables = Array.from(panel.querySelectorAll(FOCUSABLE));
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active) || (event.shiftKey && (active === first || active === panel))) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const openResource = async (source) => {
    if (openingKey) return;
    setOpeningKey(source.key);
    try {
      await downloadResource(source.link.resourceId, source.title);
    } finally {
      setOpeningKey(null);
    }
  };

  const hasUnlinked = sources.some((source) => !source.link);

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      <div className="sources-backdrop absolute inset-0 bg-text-primary/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className="sources-panel absolute inset-x-0 bottom-0 flex flex-col rounded-t-2xl bg-background shadow-lg focus:outline-none md:inset-y-0 md:left-auto md:right-0 md:w-[26rem] md:max-w-[90vw] md:rounded-none md:rounded-l-2xl"
      >
        <div className="flex items-center justify-between gap-4 border-b border-border-light px-5 py-2 md:px-6 md:pt-4">
          <h2 id={headingId} className="font-display text-xl font-semibold text-text-primary">
            Kilder
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-3 min-h-[48px] rounded-lg px-3 text-sm font-medium text-text-secondary transition-colors hover:bg-background-card hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Lukk
          </button>
        </div>

        <div className="sources-panel-body flex-1 overflow-y-auto overscroll-contain px-5 pt-3 md:px-6">
          <ul className="-mx-3 space-y-1">
            {sources.map((source) => {
              const { link } = source;
              return (
                <li key={source.key}>
                  {link && link.type === 'external' ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className={LINK_ROW}>
                      <SourceText source={source} />
                      <ExternalIcon />
                      <span className="sr-only">(åpnes i ny fane)</span>
                    </a>
                  ) : link && link.type === 'resource' ? (
                    <button
                      type="button"
                      onClick={() => openResource(source)}
                      aria-busy={openingKey === source.key}
                      className={LINK_ROW}
                    >
                      <SourceText source={source} />
                      {openingKey === source.key ? <Spinner /> : <DocumentIcon />}
                      <span className="sr-only">(åpner dokumentet)</span>
                    </button>
                  ) : (
                    <div className={ROW}>
                      <SourceText source={source} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {hasUnlinked && (
            <p className="mt-4 text-sm text-text-secondary">
              Noen kilder kan ikke åpnes her, men er brukt som grunnlag for svaret.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SourcesPanel;
